<template>
  <div class="flex flex-col h-full bg-[#F0FFFF] w-[100vw] justify-center items-center">
    <h1 class="font-bold mb-[8px] text-xl">Local login (localauth0)</h1>
    <p class="mb-[24px] text-sm text-center max-w-[420px] text-[#444]">
      Mints a real localauth0 token for the selected persona. The API validates it normally — this
      is not an auth bypass.
    </p>

    <div class="custom-select" @blur="open = false">
      <div class="selected" @click="open = !open">
        {{ selected ? selected.label : 'Select a persona to log in as' }}
      </div>
      <div class="items" :class="{ selectHide: !open }">
        <div v-for="persona of personas" :key="persona.id" @click="selectPersona(persona)">
          {{ persona.label }}
        </div>
      </div>
    </div>

    <p v-if="selected" class="mt-[12px] text-xs text-[#555] max-w-[400px] text-center">
      member {{ selected.memberId }} — {{ selected.description }}
    </p>

    <button
      :disabled="!selected || isLoading"
      class="mt-[24px] bg-[blue] rounded-lg px-[16px] py-[12px] text-[white] w-[400px]"
      :class="{ 'cursor-not-allowed': !selected || isLoading, 'bg-[grey]': !selected || isLoading }"
      @click="localLogin"
    >
      {{ isLoading ? 'Logging in...' : 'Login' }}
    </button>

    <p v-if="error" class="mt-[16px] text-sm text-[#b00020] max-w-[420px] text-center">
      {{ error }}
    </p>
  </div>
</template>

<script>
import { LOCAL_PERSONAS } from '@/config/local-personas';
import { mintPersonaToken } from '@/utils/local-auth';

/**
 * Local-only persona picker (ONE-7804).
 *
 * Rendered from pages/login/index.vue when VUE_APP_NODE_ENV === 'local'.
 * /login specifically, because the router guard sends every tokenless path
 * there in local mode — anywhere else and this would never be reachable.
 *
 * Selecting a persona mints a real localauth0 token carrying that member's
 * namespaced claims, then seeds `user.token` — which is where every store
 * module reads the bearer token from (see src/store/modules/*.js
 * `rootState.user.token`).
 *
 * localauth0 has no user store, so the persona list is a local fixture that
 * must stay in sync with the API's DB seed. See src/config/local-personas.js.
 */
export default {
  name: 'LocalLogin',
  data() {
    return {
      personas: LOCAL_PERSONAS,
      selected: null,
      open: false,
      isLoading: false,
      error: '',
    };
  },
  methods: {
    selectPersona(persona) {
      this.selected = persona;
      this.open = false;
      this.error = '';
    },
    async localLogin() {
      if (this.isLoading || !this.selected) return;

      this.isLoading = true;
      this.error = '';

      try {
        const token = await mintPersonaToken(this.selected);

        // App.vue treats a localStorage token as proof of auth in local mode,
        // and every API caller reads the bearer from the user store.
        localStorage.setItem('token', token);
        this.$store.commit('user/setToken', token);

        // Fetch the profile here rather than leaving it to /pre-loading, so an
        // API rejection (401/403) surfaces in this picker instead of stranding
        // the user on a blank loading screen.
        await this.$store.dispatch('profile/fetchProfile');

        // /pre-loading, NOT /home: it runs the real post-login gate (onboarding
        // status, pending deep link, root tab navigation) and is the same route
        // login/index.vue sends a returning local user to. Going straight to
        // /home skips that gate.
        this.$router.replace({ path: '/pre-loading' });
      } catch (err) {
        // Surface it: the previous version swallowed errors into console.log,
        // which made a failed local login look identical to nothing happening.
        this.error = err?.message || 'Local login failed';
        console.error('[LocalLogin] login failed', err);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.custom-select {
  position: relative;
  width: 400px;
  text-align: left;
  outline: none;
  height: 47px;
  line-height: 47px;
}

.custom-select .selected {
  background-color: #0a0a0a;
  border-radius: 6px;
  border: 1px solid #666666;
  color: #fff;
  padding-left: 1em;
  cursor: pointer;
  user-select: none;
}

.custom-select .selected.open {
  border: 1px solid #ad8225;
  border-radius: 6px 6px 0px 0px;
}

.custom-select .selected:after {
  position: absolute;
  content: '';
  top: 22px;
  right: 1em;
  width: 0;
  height: 0;
  border: 5px solid transparent;
  border-color: #fff transparent transparent transparent;
}

.custom-select .items {
  color: #fff;
  border-radius: 0px 0px 6px 6px;
  overflow: hidden;
  border-right: 1px solid #ad8225;
  border-left: 1px solid #ad8225;
  border-bottom: 1px solid #ad8225;
  position: absolute;
  background-color: #0a0a0a;
  left: 0;
  right: 0;
  z-index: 1;
}

.custom-select .items div {
  color: #fff;
  padding-left: 1em;
  cursor: pointer;
  user-select: none;
}

.custom-select .items div:hover {
  background-color: #ad8225;
}

.selectHide {
  display: none;
}
</style>
